import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/misc';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import type { Role } from '@/lib/types';
import { useInviteUser } from './api';

const schema = z.object({
  name: z.string().trim().min(2, 'Enter their full name'),
  email: z.string().email('Enter a valid email'),
  designation: z.string().trim().optional(),
  department: z.string().trim().optional(),
  roleIds: z.array(z.string()).min(1, 'Choose at least one role'),
});

type FormValues = z.infer<typeof schema>;

export function InviteUserDialog({
  open,
  onOpenChange,
  roles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roles: Role[];
}) {
  const invite = useInviteUser();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', designation: '', department: '', roleIds: [] },
  });

  const selectedRoles = form.watch('roleIds');

  const toggleRole = (roleId: string) => {
    const next = selectedRoles.includes(roleId)
      ? selectedRoles.filter((id) => id !== roleId)
      : [...selectedRoles, roleId];
    form.setValue('roleIds', next, { shouldValidate: true });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await invite.mutateAsync({
        name: values.name,
        email: values.email,
        roleIds: values.roleIds,
        ...(values.designation ? { designation: values.designation } : {}),
        ...(values.department ? { department: values.department } : {}),
      });
      form.reset();
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not send the invitation.');
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite someone</DialogTitle>
          <DialogDescription>
            They will get an email to set their own password. The link expires in 7 days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="invite-name">Full name</Label>
              <Input id="invite-name" autoFocus {...form.register('name')} />
              {form.formState.errors.name && (
                <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-email">Email</Label>
              <Input id="invite-email" type="email" {...form.register('email')} />
              {form.formState.errors.email && (
                <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-designation">Job title</Label>
              <Input
                id="invite-designation"
                placeholder="Optional"
                {...form.register('designation')}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-department">Department</Label>
              <Input id="invite-department" placeholder="Optional" {...form.register('department')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Roles</Label>
            <p className="text-xs text-muted-foreground">
              Roles decide what they can see and do. Someone can hold more than one.
            </p>
            <div className="scrollbar-thin max-h-52 space-y-1 overflow-y-auto rounded-md border p-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex cursor-pointer items-start gap-2.5 rounded-md p-2 hover:bg-accent"
                >
                  <Checkbox
                    checked={selectedRoles.includes(role.id)}
                    onCheckedChange={() => toggleRole(role.id)}
                    className="mt-0.5"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{role.name}</span>
                    {role.description && (
                      <span className="block text-xs text-muted-foreground">{role.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
            {form.formState.errors.roleIds && (
              <p className="text-xs text-destructive">{form.formState.errors.roleIds.message}</p>
            )}
          </div>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
